import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { fromMetricPressureMega, fromMetricTemperature, UnitSystem } from '@igus/kopla-domain';
import type { Material } from '@igus/iglidur-free-form-hub-interfaces';
import { useTranslation } from 'next-i18next';
import { isFootnoteVisible } from '../utils';
import { v4 } from 'uuid';
import { DensityCode, getUnitLabelForUnitSystem, PressureCode, TemperatureCode } from '@icommerce/units';
import { getIcommerceUnitSystemName } from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';

type MaterialPropertiesProps = {
  material: Material;
  showMoreInfo?: boolean;
  unitSystem: UnitSystem;
  openMoreInfo?: (infoType: moreInfo) => void;
};

export type moreInfo = 'thermal' | 'chemical';

type infoTable = {
  id: string;
  title: string;
  rows: { id: string; label: string; value: string | number }[];
  moreInfo?: moreInfo;
  footNote?: number;
};

export const MaterialProperties = ({ material, openMoreInfo, showMoreInfo, unitSystem }: MaterialPropertiesProps) => {
  const { t } = useTranslation(['category/productFilter', 'common']);

  const materialInfoTable = (table: infoTable) => (
    <div key={table.id} className="table">
      <div className="mt-14 flex items-center text-lg clear-both mb-3">
        <span>
          {table.title}
          {showMoreInfo && isFootnoteVisible(material, table.footNote) && <sup>{table.footNote})</sup>}
        </span>
        {showMoreInfo && table.moreInfo && (
          <span
            data-testid={`${table.title}_MORE-INFO`}
            onClick={() => openMoreInfo(table.moreInfo)}
            className="ml-3 flex cursor-pointer"
          >
            <Icon size={8} color={IconColor.BLACK} id={IconID.INFO_CIRCLE_MODERN} />
          </span>
        )}
      </div>
      {table.rows.map(row => (
        <div key={row.id} className="table-row text-sm">
          <span className="table-cell min-w-[400px] p-2 pr-10 text-left align-top">{row.label}:</span>
          <span className="kopla-formgroup__cell table-cell p-2">{row.value}</span>
        </div>
      ))}
    </div>
  );

  const temperatureUnit = getUnitLabelForUnitSystem(TemperatureCode.CELSIUS, getIcommerceUnitSystemName(unitSystem));
  const getDensity = (targetUnitSystem: UnitSystem, value: number): number => {
    const densityFactor = 27.68;
    if (targetUnitSystem === UnitSystem.metric) {
      return value;
    }
    return value / densityFactor;
  };

  const materialInformations: infoTable[] = [
    {
      id: v4(),
      title: `${t(`MATERIALS-INFO.GENERAL_PROPERTIES`, { ns: 'free-form-hub/materials' })}`,
      rows: [
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.DENSITY`, { ns: 'free-form-hub/materials' })}`,
          value: `${getDensity(unitSystem, material?.density).toFixed(
            unitSystem === UnitSystem.metric ? 2 : 4
          )} ${getUnitLabelForUnitSystem(DensityCode.GRAM_PER_CUBIC_CENTIMETER, getIcommerceUnitSystemName(unitSystem))}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.COLOR`, { ns: 'free-form-hub/materials' })}`,
          value: `${t(`COLORS.${material.color}`, { ns: 'free-form-hub/materials' })}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.MAX_MOISTURE_ABSORPTION`, { ns: 'free-form-hub/materials' })}`,
          value: material.maxMoistureAbsorption?.toFixed(2),
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.MAX_WATER_ABSORPTION`, { ns: 'free-form-hub/materials' })}`,
          value: material.maxWaterAbsorption?.toFixed(2),
        },
      ],
    },
    {
      id: v4(),
      title: `${t(`MATERIALS-INFO.MECHANICAL_PROPERTIES`, { ns: 'free-form-hub/materials' })}`,
      rows: [
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.MODULUS_OF_ELASTICITY`, { ns: 'free-form-hub/materials' })}`,
          value: material.modulusOfElasticity?.toFixed(2),
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.BENDING_STRENGTH`, { ns: 'free-form-hub/materials' })}`,
          value: `${fromMetricPressureMega(unitSystem, material.bendingStrength).toFixed(
            2
          )} ${getUnitLabelForUnitSystem(PressureCode.MEGAPASCAL, getIcommerceUnitSystemName(unitSystem))}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.MAXIMUM_SURFACE_PRESSURE`, { ns: 'free-form-hub/materials' })}`,
          value: `${fromMetricPressureMega(unitSystem, material.maxSurfacePressure).toFixed(
            2
          )} ${getUnitLabelForUnitSystem(PressureCode.MEGAPASCAL, getIcommerceUnitSystemName(unitSystem))}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.SHORE_D_HARDNESS`, { ns: 'free-form-hub/materials' })}`,
          value: material.hardness,
        },
      ],
      footNote: 3,
    },
    {
      id: v4(),
      title: `${t(`MATERIALS-INFO.THERMAL_PROPERTIES`, { ns: 'free-form-hub/materials' })}`,
      rows: [
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.UPPER_APPLICATION_TEMPERATURE`, { ns: 'free-form-hub/materials' })}`,
          value: `${fromMetricTemperature(unitSystem, material.maxTemperature)} ${temperatureUnit}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.MAX_TEMPERATURE_SHORT`, { ns: 'free-form-hub/materials' })}`,
          value: `${fromMetricTemperature(unitSystem, material.maxTemperatureShort)} ${temperatureUnit}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.MAX_ENV_TEMPERATURE_SHORT`, { ns: 'free-form-hub/materials' })}`,
          value: `${fromMetricTemperature(unitSystem, material.maxEnvironmentalTemperatureShort)} ${temperatureUnit}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.LOWER_APPLICATION_TEMPERATURE`, { ns: 'free-form-hub/materials' })}`,
          value: `${fromMetricTemperature(unitSystem, material.minTemperature)} ${temperatureUnit}`,
        },
      ],
      moreInfo: 'thermal',
    },
    {
      id: v4(),
      title: `${t(`MATERIALS-INFO.ELECTRICAL_PROPERTIES`, { ns: 'free-form-hub/materials' })}`,
      rows: [
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.SPECIFIC_VOLUME_RESISTANCE`, { ns: 'free-form-hub/materials' })}`,
          value: material.specificVolumeResistance,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.SURFACE_RESISTANCE`, { ns: 'free-form-hub/materials' })}`,
          value: material.surfaceResistance,
        },
      ],
      footNote: 3,
    },
    {
      id: v4(),
      title: `${t(`MATERIALS-INFO.FOOD_SECTOR`, { ns: 'free-form-hub/materials' })}`,
      rows: [
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.CONFORMITY_ACCORDING_TO_FDA`, { ns: 'free-form-hub/materials' })}`,
          value: `${t(`COMMON.${material.foodContactFDACompliant ? 'YES' : 'NO'}`, {
            ns: 'free-form-hub/freeFormHubCommon',
          })}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.CONFORMITY_ACCORDING_TO_EU`, { ns: 'free-form-hub/materials' })}`,
          value: `${t(`COMMON.${material.foodContactEUCompliant ? 'YES' : 'NO'}`, {
            ns: 'free-form-hub/freeFormHubCommon',
          })}`,
        },
      ],
    },
    {
      id: v4(),
      title: `${t(`MATERIALS-INFO.MORE_PROPERTIES`, { ns: 'free-form-hub/materials' })}`,
      rows: [
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.USAGE_UNDER_WATER`, { ns: 'free-form-hub/materials' })}`,
          value: `${t(`COMMON.${material.underWaterCompliant ? 'YES' : 'NO'}`, {
            ns: 'free-form-hub/freeFormHubCommon',
          })}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.DIRT_RESISTANCE`, { ns: 'free-form-hub/materials' })}`,
          value: `${t(`COMMON.${material.dirtResistant ? 'YES' : 'NO'}`, { ns: 'free-form-hub/freeFormHubCommon' })}`,
        },
        {
          id: v4(),
          label: `${t(`MATERIALS-INFO.MOULD_RESISTANT`, { ns: 'free-form-hub/materials' })}`,
          value: `${t(`COMMON.${material.mouldResistant ? 'YES' : 'NO'}`, { ns: 'free-form-hub/freeFormHubCommon' })}`,
        },
      ],
      footNote: 3,
    },
  ];

  return <div>{materialInformations.map(info => materialInfoTable(info))}</div>;
};
